import {Monorail} from '@shopify/monorail';

import {MonorailTracker as MonorailTrackerBase} from '../../../common/analytics';
import {
  LoginWithShopSdkPageName,
  LoginWithShopSdkUserAction,
  MonorailSchema,
} from '../../../common/analytics/types';
import {SHOP_DISCOUNT_FLOW} from '../constants';
import {DiscountAppVersion} from '../../../types';
import {getMonorailMiddlewareForDeprecated as getMonorailMiddleware} from '../../../common/analytics/utils';

interface MonorailTrackerParams {
  elementName: string;
  analyticsTraceId?: string;
  flowVersion: DiscountAppVersion;
  apiKey: string;
}

const middleware = getMonorailMiddleware();

export const monorailProducer =
  // eslint-disable-next-line no-process-env
  process.env.NODE_ENV === 'production'
    ? Monorail.createHttpProducer({
        production: true,
        middleware,
      })
    : Monorail.createLogProducer({
        debugMode: true,
        middleware,
      });

export default class MonorailTracker extends MonorailTrackerBase {
  private _apiKey: string;
  private _shopAccountUuid: string | undefined;

  /**
   * @param {object} params The constructor params.
   * @param {string} params.elementName The name of the element (e.g. `shop-pay-button`, `shop-login-button`, `shopify-payment-terms`).
   * @param {DiscountAppVersion} params.flowVersion The version of the Shop Login flow (eg. '1' or '2').
   * @param {string | undefined} params.apiKey The API key of the client.
   * @param {string | undefined} params.analyticsTraceId A UUID that can correlate all analytics events fired for the same user flow. I.e. Could be
   * used to correlate events between Shop JS and Pay for the Shop Login flow.
   */
  constructor({
    analyticsTraceId,
    apiKey,
    elementName,
    flowVersion,
  }: MonorailTrackerParams) {
    super({
      elementName,
      analyticsTraceId,
      flow: SHOP_DISCOUNT_FLOW,
      flowVersion,
    });
    this._apiKey = apiKey;
  }

  update({
    apiKey,
    shopAccountUuid,
    flowVersion,
  }: {
    apiKey?: string;
    shopAccountUuid?: string;
    flowVersion?: DiscountAppVersion;
  }) {
    if (apiKey) {
      this._apiKey = apiKey;
    }

    if (shopAccountUuid) {
      this._shopAccountUuid = shopAccountUuid;
    }

    if (flowVersion) {
      this._flowVersion = flowVersion;
    }
  }

  /**
   * Fired when the shop-login-button component from shop-js has finished requesting discount code from onDiscountCodeRequested.
   * @param {object} params The parameters object.
   * @param {string} params.discountCode The discount code to be saved.
   * with different implementations and potentially different performance metrics.
   */
  trackShopPayLoginWithSdkDiscountStatus({
    discountCode,
  }: {
    discountCode: string;
  }) {
    const payload = {
      apiKey: this._apiKey,
      discountCode,
      flow: SHOP_DISCOUNT_FLOW,
      flowVersion: this._flowVersion,
      discountCodeStatus: 'received',
      analyticsTraceId: this._analyticsTraceId!,
      shopPermanentDomain: this._shopPermanentDomain,
    };

    monorailProducer.produce({
      schemaId: MonorailSchema.ShopifyLoginWithShopSdkDiscountStatus,
      payload,
    });
  }

  async trackPageImpression({
    page,
  }: {
    page: LoginWithShopSdkPageName;
  }): Promise<void> {
    super.trackPageImpression({
      apiKey: this._apiKey,
      shopAccountUuid: this._shopAccountUuid,
      page,
    });
  }

  trackShopPayLoginWithShopSdkUserAction({
    userAction,
  }: {
    userAction: LoginWithShopSdkUserAction;
  }): void {
    super.trackShopPayLoginWithShopSdkUserAction({
      apiKey: this._apiKey,
      userAction,
    });
  }

  trackShopPayLoginWithSdkErrorEvents({
    errorCode,
    errorMessage,
  }: {
    errorCode: string;
    errorMessage: string;
  }): void {
    super.trackShopPayLoginWithSdkErrorEvents({
      apiKey: this._apiKey,
      errorCode,
      errorMessage,
    });
  }
}
